import { computed, nextTick, onMounted, watch, ref } from 'vue';
import { storeToRefs } from 'pinia';
import type { GenericField, OId } from '@Types';
import type { FieldInitialSettings, FieldSchema, RawField } from '@FormModule';
import { getDefaultDateFormat } from '@Helpers';
import { useSessionStore } from '@Stores';
import { ROUTES } from '@AnimalModule';
import { useForm } from '@FormModule';
import { MedicalStaff } from '@ShelterModule';

interface MedicalStaff {
  [key: string]: any;
  first_name: string;
  last_name: string;
  _id: OId;
}

const currentSchemaModels = ref<Set<GenericField>>(new Set());
const fieldsSchema = ref<FieldSchema[]>([]);
const formError = ref();
const medicalStaff = ref();

export default function () {
  const { getDetailsSchema, wasFormSubmitted } = useForm();
  const { shelterProfile } = storeToRefs(useSessionStore());

  const rawFields: RawField[] = [
    {
      label: 'Reason for Procedure',
      name: 'reason',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: 'Procedure Date',
      name: 'date',
      required: true,
      type: 'date',
      value: '',
    },
    {
      label: 'Route',
      name: 'route',
      required: false,
      type: 'select',
      value: '',
    },
    {
      label: 'Amount',
      name: 'amount',
      required: false,
      type: 'amount_unit',
      value: '',
    },
    {
      label: 'Notes',
      name: 'notes',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: 'Set Default Costs',
      name: 'track_medical_costs',
      required: false,
      type: 'toggle',
      value: '',
    },
    {
      label: 'Clinic Cost',
      name: 'clinic_cost',
      required: false,
      type: 'money',
      value: '',
    },
    {
      label: 'Retail Cost',
      name: 'retail_cost',
      required: false,
      type: 'money',
      value: '',
    },
  ];

  const fieldsSettings = computed(
    (): FieldInitialSettings => ({
      schedule_followup: {
        settings: {
          label: 'Schedule a Follow-Up Check',
        },
      },
      route: {
        edit: {
          options: Object.values(ROUTES),
        },
      },
      track_medical_costs: {
        settings: {
          show: !!shelterProfile.value?.track_medical_costs,
        },
      },
      clinic_cost: {
        settings: {
          required: false,
          show: false,
          allowZeroAsValidModel: true,
        },
      },
      retail_cost: {
        settings: {
          required: false,
          show: false,
          allowZeroAsValidModel: true,
        },
      },
    })
  );

  const trackMedicalCostsField = computed(() => fieldsSchema.value.find(({ name }) => name === 'track_medical_costs'));
  const trackMedicalCostsModel = computed(() => trackMedicalCostsField.value?.model);

  watch(trackMedicalCostsModel, () => showMedicalCostsFields());

  function showMedicalCostsFields() {
    if (!shelterProfile.value?.track_medical_costs) return;

    fieldsSchema.value.forEach((field) => {
      const medicalCostFields = ['clinic_cost', 'retail_cost'];
      if (!medicalCostFields.includes(field.name)) return;

      field.settings = {
        ...field.settings,
        show: trackMedicalCostsModel.value,
        required: trackMedicalCostsModel.value,
      };
    });
  }

  onMounted(() => fillMedicalStaff());

  function setForm() {
    if (fieldsSchema.value.length) return;

    fieldsSchema.value = getDetailsSchema(rawFields, fieldsSettings.value);
  }

  function updateSchemaModels(sourceData: any) {
    setForm();

    const toggleableFields = ['clinic_cost', 'retail_cost'];

    formError.value = null;
    wasFormSubmitted.value = false;

    fieldsSchema.value.forEach((field) => {
      const match = sourceData[field.name];
      if (toggleableFields.includes(field.name)) return;

      //prettier-ignore
      switch (field.name) {
        case 'route': return setMatchField(field, match);
        case 'reason': return setMatchField(field, match);
        case 'notes': return setMatchField(field, match);
        case 'date': return setDateField(field, sourceData);
        case 'amount': return setAmountField(field, sourceData);
         case 'track_medical_costs': {
          field.model = match;
          field.value = match;
          showMedicalCostsFields();
          nextTick(() => {
            fillMedicalCostsFields(sourceData);
          });
          break;
        }
        default: setMatchField(field, match)
      }
    });
  }

  function setMatchField(field: any, match: any) {
    field.model = match;
    field.value = match;
    field.state.errors.clear();
  }

  function setDateField(field: any, sourceData: any) {
    field.model = getDefaultDateFormat(sourceData.date || new Date());
    field.value = getDefaultDateFormat(sourceData.date || new Date());
  }

  function setAmountField(field: any, sourceData: any) {
    field.model = { amount: sourceData.amount, amount_unit: sourceData.amount_unit };
    field.value = { amount: sourceData.amount, amount_unit: sourceData.amount_unit };
  }

  function fillMedicalCostsFields(sourceData: any) {
    if (!shelterProfile.value?.track_medical_costs) return;

    fieldsSchema.value.forEach((field) => {
      const medicalCostFields = ['clinic_cost', 'retail_cost'];
      if (!medicalCostFields.includes(field.name)) return;

      field.model = sourceData.medical_costs_settings?.[field.name] || 0;
      field.value = sourceData.medical_costs_settings?.[field.name] || 0;
      field.state.errors.clear();
    });
  }

  function fillMedicalStaff() {
    MedicalStaff().then((result: any) => (medicalStaff.value = result));
  }

  function resetFieldSchemaEdit() {
    currentSchemaModels.value = new Set();
    fieldsSchema.value = [];
    formError.value = null;
    wasFormSubmitted.value = false;
  }

  return {
    currentSchemaModels,
    fieldsSchema,
    fieldsSettings,
    formError,
    resetFieldSchemaEdit,
    setForm,
    updateSchemaModels,
  };
}
